import {
  EventStreamContentType,
  fetchEventSource,
} from '@microsoft/fetch-event-source';
import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useAuth } from '@/common/contexts/AuthContext';
import {
  ChildrenDefaultProps,
  ChildrenPropTypes,
} from '@/common/proptypes/Base/Children.proptypes';
import useHubToken from '@/common/queries/mercure/useHubToken';

import { useConfig } from '../ConfigContext';

export const MAX_CONNEXION_RETRY = 3;
export const MERCURE_USER_TOPIC = 'user';
export const MERCURE_COMPANY_TOPIC = 'company';
export const MERCURE_CUSTOMER_TOPIC = 'customer';
export const MERCURE_PUBLIC_TOPIC = 'public';
export const MERCURE_MESSAGE_TYPE_PRICE_AND_STOCK = 'priceAndStock';
export const MERCURE_MESSAGE_TYPE_ORDER = 'order';
export const MERCURE_MESSAGE_TYPE_MAINTENANCE = 'maintenanceStatus';
export const MERCURE_MESSAGE_TYPE_NOTIFICATION = 'notification';

const MercureContext = createContext(null);
export const MercureContextProvider = ({ children, isActive }) => {
  const { user, isLoggedIn } = useAuth();
  const { isEbat, isChrono, isMateriaux } = useConfig();

  const getMercureHubUrl = () => {
    if (isEbat) {
      return process.env.REACT_APP_SOCKET_TOPIC_URL;
    }
    if (isChrono) {
      return process.env.REACT_APP_SOCKET_TOPIC_URL_CHRONO;
    }
    if (isMateriaux) {
      return process.env.REACT_APP_SOCKET_TOPIC_URL_MATERIAUX;
    }
    return null;
  };

  const userTopic = user?.identifier
    ? `${getMercureHubUrl()}/${MERCURE_USER_TOPIC}/${user.identifier}`
    : null;
  const publicTopic = `${getMercureHubUrl()}/${MERCURE_PUBLIC_TOPIC}`;
  const customerTopic = `${getMercureHubUrl()}/${MERCURE_COMPANY_TOPIC}/${
    user?.company_code
  }/${MERCURE_CUSTOMER_TOPIC}/${user?.customer?.id}`;

  const publicConnexionRetryRef = useRef(0);
  const privateConnexionRetryRef = useRef(0);
  const [, setPublicConnexionCount] = useState(0);
  const [privateConnexionCount, setPrivateConnexionCount] = useState(0);
  const [isConnectedPrivate, setIsConnectedPrivate] = useState(false);
  const [isConnectedPublic, setIsConnectedPublic] = useState(false);

  const prevHubTokenRef = useRef();
  const { data: hubToken } = useHubToken(
    privateConnexionCount,
    isLoggedIn && isActive,
  );
  const subscribersRef = useRef({});

  const messageSubscribe = useCallback(
    (subscriberId, topic, type, messageHandler) => {
      const nextSubscribers = { ...subscribersRef.current };
      if (!nextSubscribers?.[topic]) {
        nextSubscribers[topic] = {};
      } else {
        nextSubscribers[topic] = { ...subscribersRef.current[topic] };
      }
      if (!nextSubscribers[topic]?.[type]) {
        nextSubscribers[topic][type] = {};
      } else {
        nextSubscribers[topic][type] = {
          ...subscribersRef.current[topic][type],
        };
      }
      nextSubscribers[topic][type][subscriberId] = messageHandler;
      subscribersRef.current = nextSubscribers;
    },
    [],
  );
  const messageUnsubscribe = useCallback((subscriberId, topic, type) => {
    const nextSubscribers = { ...subscribersRef.current };
    if (nextSubscribers?.[topic]) {
      if (nextSubscribers[topic]?.[type]) {
        nextSubscribers[topic][type] = {
          ...subscribersRef.current[topic][type],
        };
        delete nextSubscribers[topic][type][subscriberId];
      }
    }
    subscribersRef.current = nextSubscribers;
  }, []);

  const pushMessage = (topic, data, isPrivate = false) => {
    if (!process.env.REACT_APP_SOCKET_HUB_URL || !hubToken || !topic) return;
    const formattedData = { ...data };
    if (!formattedData?.topic) formattedData.topic = topic;
    const url = new URL(process.env.REACT_APP_SOCKET_HUB_URL);
    const body = new URLSearchParams({
      data: JSON.stringify(formattedData),
    });

    body.append('topic', topic);
    if (isPrivate) {
      body.append('private', 'on');
    }

    const opt = {
      method: 'POST',
      headers: { Authorization: `Bearer ${hubToken}` },
      body,
    };

    fetch(url, opt).catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e);
    });
  };

  const handleMessage = (event) => {
    const parsedMessage = JSON.parse(event.data);
    const topic = parsedMessage?.topic;
    const type = parsedMessage?.type;
    if (subscribersRef.current?.[topic]?.[type]) {
      Object.keys(subscribersRef.current[topic][type]).forEach(
        (subscriberId) => {
          subscribersRef.current[topic][type][subscriberId](parsedMessage);
        },
      );
    }
  };

  const subscribeToTopic = useCallback(
    async (topic, onOpenSuccess, headers = {}) => {
      const ctrl = new AbortController();
      const { signal } = ctrl;
      const url = new URL(process.env.REACT_APP_SOCKET_HUB_URL);
      url.searchParams.append('topic', topic);
      return fetchEventSource(url.toString(), {
        signal,
        openWhenHidden: true,
        headers,
        async onopen(response) {
          if (
            response.ok &&
            response.headers.get('content-type') === EventStreamContentType
          ) {
            onOpenSuccess();
            return;
          }
          if (
            response.status >= 400 &&
            response.status < 500 &&
            response.status !== 429
          ) {
            throw new Error('fatal');
          } else {
            throw new Error('retry');
          }
        },
        onmessage: handleMessage,
        onclose() {
          throw new Error('retry');
        },
        onerror(err) {
          if (err instanceof Error) {
            throw err;
          }
        },
      });
    },
    [],
  );

  useEffect(() => {
    const token = hubToken?.token;

    if (token && token !== prevHubTokenRef.current) {
      prevHubTokenRef.current = token;
    }
  }, [hubToken]);

  // subscribe to public topic
  useEffect(() => {
    const onOpenSuccess = () => {
      publicConnexionRetryRef.current = 0;
      setIsConnectedPublic(true);
    };

    subscribeToTopic(publicTopic, onOpenSuccess).catch((e) => {
      if (
        e.message === 'retry' &&
        publicConnexionRetryRef.current < MAX_CONNEXION_RETRY
      ) {
        publicConnexionRetryRef.current += 1;
        setPublicConnexionCount((prev) => prev + 1);
      }
      setIsConnectedPublic(false);
    });
  }, [publicTopic, setPublicConnexionCount, subscribeToTopic]);

  // subscribe to private topic
  useEffect(() => {
    if (
      !process.env.REACT_APP_SOCKET_HUB_URL ||
      !hubToken?.token ||
      !userTopic
    ) {
      return;
    }

    if (userTopic && hubToken) {
      const onOpenSuccess = () => {
        privateConnexionRetryRef.current = 0;
        setIsConnectedPrivate(true);
      };
      subscribeToTopic(userTopic, onOpenSuccess, {
        authorization: `Bearer ${hubToken.token}`,
      }).catch((e) => {
        if (
          e.message === 'retry' &&
          privateConnexionRetryRef.current < MAX_CONNEXION_RETRY
        ) {
          privateConnexionRetryRef.current += 1;
          setPrivateConnexionCount((prev) => prev + 1);
        }
        setIsConnectedPrivate(false);
      });

      subscribeToTopic(customerTopic, onOpenSuccess, {
        authorization: `Bearer ${hubToken.token}`,
      }).catch((e) => {
        if (
          e.message === 'retry' &&
          privateConnexionRetryRef.current < MAX_CONNEXION_RETRY
        ) {
          privateConnexionRetryRef.current += 1;
          setPrivateConnexionCount((prev) => prev + 1);
        }
        setIsConnectedPrivate(false);
      });
    }
  }, [customerTopic, hubToken, subscribeToTopic, userTopic]);

  return (
    <MercureContext.Provider
      value={{
        messageSubscribe,
        messageUnsubscribe,
        pushMessage,
        userTopic,
        publicTopic,
        customerTopic,
        isConnectedPrivate,
        isConnectedPublic,
      }}
    >
      {children}
    </MercureContext.Provider>
  );
};

export const useMercureContext = () => useContext(MercureContext);

MercureContextProvider.defaultProps = {
  ...ChildrenDefaultProps,
  isActive: true,
};

MercureContextProvider.propTypes = {
  ...ChildrenPropTypes,
  isActive: PropTypes.bool,
};
